import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CrownIcon } from "../../../../assets/images/icons/CrownIcon";
import { useAppStore } from "../../../../hooks/useStores";

import i18n from "i18next";
import RouterConstants from "../../../../constants/RouterConstants.ts";
import { IMAGE_REFERENCE_DISABLED_MODELS } from "../../../../constants/TextToImageConstants.ts";
import { OrgFeatures } from "../../../../models/billing.ts";
import iframeStore from "../../../../pages/IframeNew/iframe-store.tsx";
import { TgModel } from "../../../../pages/NewTG/Models/store/tg-model-store.tsx";
import { isBriaMember } from "../../../../utils";
import CustomSingleSelect, {
	IGroup,
	IGroupOption,
	SingleSelectGroupedDropDownIProps,
} from "../../CustomSingleSelectDropdown/SingleSelectGroupedDropDown.tsx";
import { ModelDropdownKey } from "./NewTextToImageModelsDropdown.tsx";
import styles from "./TextToImageModelsDropdown.module.scss";

type TextToImageModelsDropdownProps = {
	onFoundationModelsSuccess?: (foundationModels: IGroupOption[]) => void;
	onTailoredModelsSuccess?: (tailoredModels: IGroupOption[]) => void;
	isVector?: boolean;
};

const TextToImageModelsDropdown = ({
	handleChange,
	onFoundationModelsSuccess,
	onTailoredModelsSuccess,
	isVector = false,
	showFoundationModels = true,
	...rest
}: Partial<SingleSelectGroupedDropDownIProps<string | string[]>> & TextToImageModelsDropdownProps) => {
	const { t } = useTranslation("translation", {
		keyPrefix: "playground.textToImage.config.models.dropdownLabels",
	});
	const { textToImageStore, tgStore, authStore } = useAppStore();

	const [foundationModelsItems, setFoundationModelsItems] = useState<IGroupOption[]>([]);
	const [tailoredModelsItems, setTailoredModelsItems] = useState<IGroupOption[]>([]);
	const modelGroups: IGroup[] = [
		{
			name: t("foundationModels"),
			options: foundationModelsItems,
			loading: textToImageStore.loadingModels,
		},
		{
			name: t("tailoredModels"),
			options: tailoredModelsItems,
			loading: tgStore.tgModelStore.loadingModelsList || authStore.isLoadingOrgSubscriptions,
		},
	];

	useEffect(() => {
		const fetchModels = async () => {
			const foundationItems = showFoundationModels ? await createFoundationItems() : [];
			if (isVector && showFoundationModels) {
				setFoundationModelsItems(foundationItems.filter((item) => item.id !== "hd_2.2"));
			} else {
				setFoundationModelsItems(foundationItems);
			}
			onFoundationModelsSuccess?.(foundationItems);

			const tailoredItems = await createTailoredItems();
			setTailoredModelsItems(tailoredItems);
			onTailoredModelsSuccess?.(tailoredItems);
		};
		if (!authStore.isLoadingOrgSubscriptions) {
			fetchModels();
		}
	}, [textToImageStore.imageReference, authStore.isLoadingOrgSubscriptions]);

	useEffect(() => {
		if (Object.keys(textToImageStore.models).length) {
			if (
				(!textToImageStore.config.model || !textToImageStore.config.model_version) &&
				!textToImageStore.config.tailored_model_id
			) {
				if (iframeStore.isIframe()) {
					textToImageStore.handleConfigChange("model", "fast");
					textToImageStore.handleConfigChange("model_version", "2.3");
				}
			}
		}
	}, [textToImageStore.models]);

	const handleModelChange = (event: any) => {
		const selectedOption = modelGroups
			.map((group) => group.options)
			.flat()
			.find((option) => option?.id === event.target.value);

		if (selectedOption) {
			if (
				!selectedOption.extraData?.isTailoredModel ||
				(selectedOption.extraData?.isTailoredModel &&
					(authStore.isFeatureEnabled(OrgFeatures.TAILORED_GENERATION) || isBriaMember(authStore.user)))
			) {
				const isTailoredModel = selectedOption && selectedOption?.extraData?.isTailoredModel;
				if (!isTailoredModel) {
					const model = getModelName(event.target.value as string);
					const modelVersion = getModelVersion(event.target.value as string);
					if (
						textToImageStore.isImageReferenceEnabled() &&
						IMAGE_REFERENCE_DISABLED_MODELS.includes((model + "_" + modelVersion) as ModelDropdownKey) &&
						textToImageStore.imageReference.url
					) {
						textToImageStore.handleDeleteImageReference();
					}
				}

				handleChange(event, selectedOption);
			} else {
				window.open(`${RouterConstants.CONSOLE.fullPath}?pricing=true`);
			}
		}
	};

	const createFoundationItems = async (): Promise<IGroupOption[]> => {
		return Object.entries(await textToImageStore.getModels())
			.flatMap(([modelName, model]) =>
				model.versions.map((version: string) => ({
					id: getModelKey(modelName, version),
					key:
						iframeStore?.iframe?.config.image_generation_config?.enabled_foundation_models?.[modelName][
							version
						] ?? getModelDefaultLabel(modelName, version),
					value: getModelKey(modelName, version),
					label:
						iframeStore?.iframe?.config.image_generation_config?.enabled_foundation_models?.[modelName][
							version
						] ?? getModelDefaultLabel(modelName, version),
				})),
			)
			.slice()
			.sort(customfoundationModelsSort);
	};

	const createTailoredItems = async (): Promise<IGroupOption[]> => {
		const createItem = (model: TgModel): IGroupOption => ({
			id: model.id.toString(),
			value: model.id.toString(),
			key: model.name ?? "",
			label: model.name ?? "",
			description: model.description ?? "",
			extraData: {
				isTailoredModel: true,
				model_id: model.id.toString() ?? "",
				training_version: model.training_version,
				generation_prefix: model.generation_prefix,
			},
			icon:
				!isBriaMember(authStore.user) && !authStore.isFeatureEnabled(OrgFeatures.TAILORED_GENERATION) ? (
					<CrownIcon />
				) : (
					""
				),
		});

		try {
			if (tgStore.tgModelStore.modelsList?.length) {
				return tgStore.tgModelStore.modelsList
					.filter((model) => model.training_version === "fibo")
					.map((model) => createItem(model));
			}

			const loadedModels = await tgStore.tgModelStore.listAllModels("Completed");
			if (!loadedModels) return [];

			return loadedModels.filter((model) => model.training_version === "fibo").map((model) => createItem(model));
		} catch (error) {
			console.error("Error loading tailored models:", error);
			return [];
		}
	};

	function customfoundationModelsSort(item1: IGroupOption, item2: IGroupOption) {
		const order: ModelDropdownKey[] = [
			"fibo",
			"fibo_lite",
			"base_3.2",
			"base_3.1",
			"fast_2.3",
			"fast_2.2",
			"hd_2.2",
		];
		return order.indexOf(item1.id as ModelDropdownKey) - order.indexOf(item2.id as ModelDropdownKey);
	}

	return (
		<CustomSingleSelect
			handleChange={handleModelChange}
			groups={modelGroups}
			paddingClass={styles.customSingleSelectRoot}
			selectStyle={styles.selectStyle}
			menuMaxHeight="50vh"
			className={styles.menuItemRoot}
			sortAlphabetically={false}
			hideEmptyGroups={true}
			{...rest}
		/>
	);
};

export default observer(TextToImageModelsDropdown);

export type TextToImageModels = {
	[model: string]: {
		versions: string[];
		image_influence: { min: number; max: number; default: number };
		steps: { min: number; max: number; default: number };
		max_results: number;
	};
};

export const getModelKey = (model: string, version: string) => {
	return `${model}_${version}`;
};

export const getModelName = (modelKey: string) => {
	return modelKey.split("_")[0];
};
export const getModelVersion = (modelKey: string) => {
	return modelKey.split("_")[1];
};

export const getModelDefaultLabel = (model: string, version: string) => {
	const formattedVersion =
		model === "fibo" && version ? version.charAt(0).toUpperCase() + version.slice(1).toLowerCase() : version;
	return `${i18n.t(`playground.textToImage.config.models.dropdownLabels.${model}`, { version: formattedVersion })}`;
};
